import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, Pencil, Upload, Trash2, ChevronDown, Send, Sparkles, Clapperboard, Users, Play, BookOpen, MessageSquareText, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api, formatDate, formatPrice, scoreBg, videoStreamUrl } from '../lib/api';
import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { UserAvatar } from '../components/UserAvatar';
import { Skeleton } from '../components/ui/skeleton';

const CourseForm = ({ initial, onSaved, onClose }) => {
  const { t } = useLanguage();
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [price, setPrice] = useState(initial ? String((initial.price_cents || 0) / 100) : '0');
  const [coverUrl, setCoverUrl] = useState(initial?.cover_url || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = { title: title.trim(), description: description.trim(), price_eur: parseFloat(price) || 0, cover_url: coverUrl.trim() };
      const { data } = initial
        ? await api.put(`/academy/courses/${initial.course_id}`, body)
        : await api.post('/academy/courses', body);
      onSaved(data, !initial);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'object' ? JSON.stringify(detail) : detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="course-form">
      <div className="space-y-1.5">
        <label className="font-mono text-[11px] uppercase tracking-wider text-white/45">{t('courseTitle')}</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} data-testid="course-form-title"
          className="w-full h-11 rounded-[14px] bg-white/[0.05] border border-white/10 px-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none" />
      </div>
      <div className="space-y-1.5">
        <label className="font-mono text-[11px] uppercase tracking-wider text-white/45">{t('courseDescription')}</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={2000} data-testid="course-form-description"
          className="w-full rounded-[14px] bg-white/[0.05] border border-white/10 p-3.5 text-sm text-white placeholder:text-white/30 resize-none focus:outline-none" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="font-mono text-[11px] uppercase tracking-wider text-white/45">{t('coursePrice')}</label>
          <input type="number" min="0" max="500" step="0.5" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="course-form-price"
            className="w-full h-11 rounded-[14px] bg-white/[0.05] border border-white/10 px-3.5 text-sm text-white focus:outline-none" />
        </div>
        <div className="space-y-1.5">
          <label className="font-mono text-[11px] uppercase tracking-wider text-white/45">{t('coverUrl')}</label>
          <input value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)} maxLength={500} data-testid="course-form-cover"
            className="w-full h-11 rounded-[14px] bg-white/[0.05] border border-white/10 px-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none" />
        </div>
      </div>
      <button onClick={save} disabled={saving || title.trim().length < 3} data-testid="course-form-save"
        className="ss-btn-primary w-full h-11 text-sm font-bold font-display flex items-center justify-center gap-2 disabled:opacity-50">
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Pencil size={15} />} {t('saveCourse')}
      </button>
    </div>
  );
};

const ModuleUploader = ({ courseId, onUploaded }) => {
  const { t } = useLanguage();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const upload = async () => {
    if (!file || !title.trim()) return;
    setUploading(true);
    setProgress(0);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title.trim());
    fd.append('description', description.trim());
    try {
      const { data } = await api.post(`/academy/courses/${courseId}/modules`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
      });
      onUploaded(data);
      setTitle('');
      setDescription('');
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      toast.success(t('uploadModule') + ' \u2713');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || 'Error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2.5 rounded-[16px] border border-dashed border-white/15 p-3.5" data-testid="module-uploader">
      <div className="font-mono text-[11px] uppercase tracking-wider text-white/45">{t('addModule')}</div>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('moduleTitle')} maxLength={120} data-testid="module-title-input"
        className="w-full h-10 rounded-[14px] bg-white/[0.05] border border-white/10 px-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none" />
      <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('moduleDescription')} maxLength={1000} data-testid="module-description-input"
        className="w-full h-10 rounded-[14px] bg-white/[0.05] border border-white/10 px-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none" />
      <div className="flex items-center gap-2">
        <input ref={inputRef} type="file" accept="video/*" data-testid="module-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="flex-1 text-xs text-white/60 file:mr-3 file:rounded-[12px] file:border-0 file:bg-white/[0.1] file:px-3 file:py-2 file:text-xs file:text-white file:cursor-pointer" />
        <button onClick={upload} disabled={uploading || !file || !title.trim()} data-testid="module-upload-button"
          className="ss-btn-glass h-10 px-4 text-xs font-medium flex items-center gap-1.5 disabled:opacity-40">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {t('uploadModule')}
        </button>
      </div>
      <p className="font-mono text-[10px] text-white/35">{t('moduleVideo')}</p>
      {uploading && (
        <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
          <div className="h-full rounded-full bg-[#22C55E] transition-[width] duration-300" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------------------
 * Tab sub-components (extracted to keep CoachStudio lean and testable)
 * ------------------------------------------------------------------------- */

const CoachCourseCard = ({ course: c, isOpen, modules, onToggle, onEdit, onTogglePublish, publishing, onDeleteModule, onModuleUploaded, t }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="ss-card p-4" data-testid={`coach-course-${c.course_id}`}>
    <div className="flex items-center gap-3">
      <button onClick={() => onToggle(c)} className="flex-1 min-w-0 text-left flex items-center gap-3" data-testid={`coach-course-expand-${c.course_id}`}>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate flex items-center gap-2">
            {c.title}
            <span className={`font-mono text-[9px] uppercase border rounded-[8px] px-1.5 py-px ${c.published ? 'bg-[rgba(34,197,94,0.14)] text-[#22C55E] border-[rgba(34,197,94,0.3)]' : 'bg-white/[0.07] text-white/50 border-white/10'}`} data-testid={`coach-course-status-${c.course_id}`}>
              {c.published ? t('published') : t('draft')}
            </span>
          </div>
          <div className="font-mono text-[10px] text-white/40 mt-0.5 flex items-center gap-3">
            <span>{c.price_cents > 0 ? formatPrice(c.price_cents, c.currency) : t('priceFree')}</span>
            <span className="flex items-center gap-1"><Clapperboard size={10} /> {c.modules_count}</span>
            <span className="flex items-center gap-1"><Users size={10} /> {c.enrollments_count}</span>
          </div>
        </div>
        <ChevronDown size={16} className={`text-white/40 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
    </div>

    {isOpen && (
      <div className="mt-3.5 space-y-3">
        <div className="flex gap-2">
          <button onClick={() => onEdit(c)} data-testid={`coach-course-edit-${c.course_id}`}
            className="ss-btn-glass h-9 px-3.5 text-xs font-medium flex items-center gap-1.5">
            <Pencil size={12} /> {t('editCourse')}
          </button>
          <button onClick={() => onTogglePublish(c)} disabled={publishing} data-testid={`coach-course-publish-${c.course_id}`}
            className={`${c.published ? 'ss-btn-glass' : 'ss-btn-primary'} h-9 px-3.5 text-xs font-medium flex items-center gap-1.5`}>
            {publishing ? <Loader2 size={12} className="animate-spin" /> : c.published ? <EyeOff size={12} /> : <Eye size={12} />}
            {c.published ? t('unpublish') : t('publish')}
          </button>
        </div>

        {modules === undefined ? (
          <Skeleton className="h-12 w-full bg-white/[0.05] rounded-[14px]" />
        ) : (
          <div className="space-y-1.5">
            {modules.map((m, i) => (
              <div key={m.module_id} className="flex items-center gap-2.5 rounded-[14px] bg-white/[0.03] border border-white/[0.07] px-3 py-2" data-testid={`coach-module-${m.module_id}`}>
                <span className="font-mono text-[10px] text-white/40">{String(i + 1).padStart(2, '0')}</span>
                <span className="flex-1 text-xs text-white/85 truncate">{m.title}</span>
                <button onClick={() => onDeleteModule(c.course_id, m.module_id)} data-testid={`coach-module-delete-${m.module_id}`}
                  className="w-7 h-7 rounded-[10px] flex items-center justify-center text-white/30 hover:text-[#E76F7B] transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        <ModuleUploader courseId={c.course_id} onUploaded={(m) => onModuleUploaded(c.course_id, m)} />
      </div>
    )}
  </motion.div>
);

const ReviewCard = ({ r, lang, t, draft, onDraftChange, onSendFeedback, sending, onWatch }) => (
  <div className="ss-card p-4 space-y-3" data-testid={`coach-review-${r.review_id}`}>
    <div className="flex items-center gap-3">
      <UserAvatar src={r.player?.picture} alt={r.player?.name} size={40} rounded={14} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-white truncate">{r.player?.name}</div>
        <div className="font-mono text-[10px] text-white/40 truncate">
          {(lang === 'it' ? r.report?.move_label_it : r.report?.move_label_en) || '\u2014'} · {formatDate(r.created_at, lang)}
        </div>
      </div>
      {r.report?.overall_score != null && (
        <span className={`border rounded-[10px] px-1.5 py-0.5 font-mono text-[11px] tabular-nums ${scoreBg(r.report.overall_score)}`}>{r.report.overall_score}</span>
      )}
      <span className={`border rounded-[10px] px-2 py-0.5 font-mono text-[10px] ${r.status === 'completed' ? 'bg-[rgba(34,197,94,0.14)] text-[#22C55E] border-[rgba(34,197,94,0.3)]' : 'bg-white/[0.1] text-white border-white/20'}`}>
        {r.status === 'completed' ? t('reviewCompleted') : t('reviewPaid')}
      </span>
    </div>

    {r.note && (
      <div className="text-xs text-white/65 bg-white/[0.04] border border-white/[0.07] rounded-[14px] p-3">
        <span className="font-mono text-[9px] uppercase text-white/40 block mb-1">{t('playerNote')}</span>
        {r.note}
      </div>
    )}

    {r.ai_preeval && (
      <div className="text-xs text-white/65 bg-white/[0.04] border border-white/[0.07] rounded-[14px] p-3" data-testid={`coach-review-preeval-${r.review_id}`}>
        <span className="font-mono text-[9px] uppercase text-white/40 block mb-1 flex items-center gap-1"><Sparkles size={9} /> {t('aiPreeval')}</span>
        <p className="whitespace-pre-wrap leading-relaxed">{r.ai_preeval}</p>
      </div>
    )}

    <button onClick={() => onWatch(r)} data-testid={`coach-review-watch-${r.review_id}`}
      className="ss-btn-glass h-10 px-4 text-xs font-medium flex items-center gap-2">
      <Play size={13} /> {t('watchStudentClip')}
    </button>

    {r.status !== 'completed' ? (
      <div className="space-y-2">
        <textarea
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          placeholder={t('writeFeedback')}
          rows={3}
          maxLength={3000}
          data-testid={`coach-review-feedback-input-${r.review_id}`}
          className="w-full rounded-[14px] bg-white/[0.05] border border-white/10 p-3.5 text-sm text-white placeholder:text-white/30 resize-none focus:outline-none"
        />
        <button
          onClick={() => onSendFeedback(r)}
          disabled={sending || draft.trim().length < 10}
          data-testid={`coach-review-send-feedback-${r.review_id}`}
          className="ss-btn-primary h-10 px-5 text-xs font-bold font-display flex items-center gap-2 disabled:opacity-50"
        >
          {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {t('sendFeedback')}
        </button>
      </div>
    ) : (
      r.coach_feedback && (
        <p className="text-sm text-white/80 leading-relaxed bg-white/[0.04] border border-white/[0.07] rounded-[14px] p-3.5">{r.coach_feedback}</p>
      )
    )}
  </div>
);

const AssistantPanel = ({ chat, chatBusy, chatInput, setChatInput, sendChat, chatEndRef, t }) => (
  <div className="ss-card p-4 flex flex-col h-[60dvh]" data-testid="coach-assistant-panel">
    <div className="flex-1 overflow-y-auto space-y-3 pr-1">
      {chat.length === 0 && (
        <div className="h-full flex flex-col items-center justify-center text-center gap-2 py-10">
          <Sparkles size={22} className="text-white/40" />
          <p className="text-xs text-white/45 font-mono max-w-xs">{t('aiAssistantPlaceholder')}</p>
        </div>
      )}
      {chat.map((m, i) => (
        <div key={m.id} className={`max-w-[85%] rounded-[16px] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          m.role === 'user' ? 'ml-auto bg-white/[0.1] text-white' : 'bg-white/[0.04] border border-white/[0.07] text-white/85'
        }`} data-testid={`coach-chat-message-${i}`}>
          {m.content}
        </div>
      ))}
      {chatBusy && (
        <div className="flex items-center gap-2 text-white/45 text-xs"><Loader2 size={13} className="animate-spin" /> …</div>
      )}
      <div ref={chatEndRef} />
    </div>
    <div className="pt-3 border-t border-white/[0.07] flex items-center gap-2 mt-3">
      <input
        value={chatInput}
        onChange={(e) => setChatInput(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && sendChat()}
        placeholder={t('aiAssistantPlaceholder')}
        data-testid="coach-chat-input"
        className="flex-1 h-11 rounded-[14px] bg-white/[0.05] border border-white/10 px-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none"
      />
      <button onClick={sendChat} disabled={chatBusy || !chatInput.trim()} data-testid="coach-chat-send"
        className="ss-btn-primary w-11 h-11 flex items-center justify-center disabled:opacity-40" aria-label={t('send')}>
        <Send size={16} />
      </button>
    </div>
  </div>
);

export default function CoachStudio() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { user, loading } = useAuth();
  const [tab, setTab] = useState('courses');
  const [courses, setCourses] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [modulesByCourse, setModulesByCourse] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [publishing, setPublishing] = useState(null);

  const [reviews, setReviews] = useState(null);
  const [watchReview, setWatchReview] = useState(null);
  const [feedbackDrafts, setFeedbackDrafts] = useState({});
  const [sendingFeedback, setSendingFeedback] = useState(null);

  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const chatEndRef = useRef(null);

  const role = user?.role;

  useEffect(() => {
    if (!loading && user && role !== 'coach' && role !== 'admin') navigate('/become-coach');
  }, [loading, user, role, navigate]);

  useEffect(() => {
    if (role !== 'coach' && role !== 'admin') return;
    (async () => {
      try {
        const { data } = await api.get('/academy/coach/courses');
        setCourses(data.items);
      } catch {
        setCourses([]);
      }
    })();
  }, [role]);

  useEffect(() => {
    if (tab === 'reviews' && reviews === null && (role === 'coach' || role === 'admin')) {
      (async () => {
        try {
          const { data } = await api.get('/academy/coach/reviews');
          setReviews(data.items);
        } catch {
          setReviews([]);
        }
      })();
    }
  }, [tab, reviews, role]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat]);

  const loadModules = async (courseId) => {
    try {
      const { data } = await api.get(`/academy/courses/${courseId}`);
      setModulesByCourse((prev) => ({ ...prev, [courseId]: data.modules || [] }));
    } catch {
      setModulesByCourse((prev) => ({ ...prev, [courseId]: [] }));
    }
  };

  const toggleExpand = (course) => {
    const next = expanded === course.course_id ? null : course.course_id;
    setExpanded(next);
    if (next && !modulesByCourse[next]) loadModules(next);
  };

  const onSaved = (course, isNew) => {
    if (isNew) setCourses((prev) => [course, ...(prev || [])]);
    else setCourses((prev) => prev.map((c) => (c.course_id === course.course_id ? { ...c, ...course } : c)));
    toast.success(t('saveCourse') + ' \u2713');
  };

  const togglePublish = async (course) => {
    setPublishing(course.course_id);
    try {
      if (course.published) {
        await api.post(`/academy/courses/${course.course_id}/unpublish`);
        setCourses((prev) => prev.map((c) => (c.course_id === course.course_id ? { ...c, published: false } : c)));
      } else {
        await api.post(`/academy/courses/${course.course_id}/publish`);
        setCourses((prev) => prev.map((c) => (c.course_id === course.course_id ? { ...c, published: true } : c)));
        toast.success(t('published'));
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || 'Error');
    } finally {
      setPublishing(null);
    }
  };

  const deleteModule = async (courseId, moduleId) => {
    try {
      await api.delete(`/academy/courses/${courseId}/modules/${moduleId}`);
      setModulesByCourse((prev) => ({ ...prev, [courseId]: (prev[courseId] || []).filter((m) => m.module_id !== moduleId) }));
      setCourses((prev) => prev.map((c) => (c.course_id === courseId ? { ...c, modules_count: Math.max(0, c.modules_count - 1) } : c)));
    } catch {
      toast.error('Error');
    }
  };

  const onModuleUploaded = (courseId, m) => {
    setModulesByCourse((prev) => ({ ...prev, [courseId]: [...(prev[courseId] || []), m] }));
    setCourses((prev) => prev.map((x) => (x.course_id === courseId ? { ...x, modules_count: x.modules_count + 1 } : x)));
  };

  const sendFeedback = async (review) => {
    const text = (feedbackDrafts[review.review_id] || '').trim();
    if (text.length < 10) return;
    setSendingFeedback(review.review_id);
    try {
      await api.post(`/academy/coach/reviews/${review.review_id}/feedback`, { feedback: text });
      setReviews((prev) => prev.map((r) => (r.review_id === review.review_id ? { ...r, status: 'completed', coach_feedback: text } : r)));
      toast.success(t('sendFeedback') + ' \u2713');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSendingFeedback(null);
    }
  };

  const chatMsgId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || chatBusy) return;
    setChat((prev) => [...prev, { id: chatMsgId(), role: 'user', content: msg }]);
    setChatInput('');
    setChatBusy(true);
    try {
      const history = chat.map(({ role: r, content }) => ({ role: r, content }));
      const { data } = await api.post('/academy/coach/assistant', { message: msg, history, lang });
      setChat((prev) => [...prev, { id: chatMsgId(), role: 'assistant', content: data.reply }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI error');
    } finally {
      setChatBusy(false);
    }
  };

  if (loading || (role !== 'coach' && role !== 'admin')) return null;

  const tabs = [
    { id: 'courses', label: t('myCourses'), icon: BookOpen },
    { id: 'reviews', label: t('reviewQueue'), icon: MessageSquareText },
    { id: 'assistant', label: t('aiAssistant'), icon: Sparkles },
  ];

  return (
    <div className="space-y-4 max-w-3xl mx-auto" data-testid="coach-studio-page">
      <div className="pt-2 flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight text-white">{t('coachArea')}</h1>
          <p className="text-sm text-white/55 mt-1">{t('academySub')}</p>
        </div>
        <button onClick={() => { setEditCourse(null); setFormOpen(true); }} data-testid="coach-create-course-button"
          className="ss-btn-primary h-10 px-4 text-xs font-bold font-display flex items-center gap-1.5 shrink-0">
          <Plus size={15} /> {t('createCourse')}
        </button>
      </div>

      <div className="flex gap-2" data-testid="coach-tabs">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`coach-tab-${id}`}
            className={`ss-chip px-3.5 py-1.5 text-xs font-medium flex items-center gap-1.5 ${tab === id ? 'ss-chip-active' : ''}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {tab === 'courses' && (
        courses === null ? (
          <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-20 w-full bg-white/[0.05] rounded-[20px]" />)}</div>
        ) : courses.length === 0 ? (
          <div className="ss-card p-10 text-center space-y-3" data-testid="coach-courses-empty">
            <span className="inline-flex w-14 h-14 rounded-[16px] bg-white/[0.07] border border-white/12 items-center justify-center">
              <BookOpen size={24} className="text-white" />
            </span>
            <p className="text-sm text-white/55">{t('createCourse')}</p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="coach-courses-list">
            {courses.map((c) => (
              <CoachCourseCard
                key={c.course_id}
                course={c}
                isOpen={expanded === c.course_id}
                modules={modulesByCourse[c.course_id]}
                onToggle={toggleExpand}
                onEdit={(course) => { setEditCourse(course); setFormOpen(true); }}
                onTogglePublish={togglePublish}
                publishing={publishing === c.course_id}
                onDeleteModule={deleteModule}
                onModuleUploaded={onModuleUploaded}
                t={t}
              />
            ))}
          </div>
        )
      )}

      {tab === 'reviews' && (
        reviews === null ? (
          <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-20 w-full bg-white/[0.05] rounded-[20px]" />)}</div>
        ) : reviews.length === 0 ? (
          <div className="ss-card p-10 text-center space-y-3" data-testid="coach-reviews-empty">
            <span className="inline-flex w-14 h-14 rounded-[16px] bg-white/[0.07] border border-white/12 items-center justify-center">
              <MessageSquareText size={24} className="text-white" />
            </span>
            <p className="text-sm text-white/55">{t('noReviews')}</p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="coach-reviews-list">
            {reviews.map((r) => (
              <ReviewCard
                key={r.review_id}
                r={r}
                lang={lang}
                t={t}
                draft={feedbackDrafts[r.review_id] || ''}
                onDraftChange={(v) => setFeedbackDrafts((prev) => ({ ...prev, [r.review_id]: v }))}
                onSendFeedback={sendFeedback}
                sending={sendingFeedback === r.review_id}
                onWatch={setWatchReview}
              />
            ))}
          </div>
        )
      )}

      {tab === 'assistant' && (
        <AssistantPanel chat={chat} chatBusy={chatBusy} chatInput={chatInput} setChatInput={setChatInput} sendChat={sendChat} chatEndRef={chatEndRef} t={t} />
      )}

      {/* create/edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="bg-[#1E1E1E] border-white/10 rounded-[20px] max-w-md" data-testid="coach-course-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{editCourse ? t('editCourse') : t('createCourse')}</DialogTitle>
          </DialogHeader>
          <CourseForm initial={editCourse} onSaved={onSaved} onClose={() => setFormOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* watch student clip dialog */}
      <Dialog open={!!watchReview} onOpenChange={(open) => !open && setWatchReview(null)}>
        <DialogContent className="bg-[#1E1E1E] border-white/10 rounded-[20px] max-w-lg" data-testid="coach-watch-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{watchReview?.player?.name}</DialogTitle>
          </DialogHeader>
          {watchReview && (
            <div className="rounded-[16px] overflow-hidden bg-black aspect-video">
              <video src={videoStreamUrl(watchReview.video_id)} controls autoPlay playsInline muted className="w-full h-full object-contain" data-testid="coach-watch-video" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
